import React, { useEffect } from 'react';
import { styled } from '@compiled/react';
import Avatar from '@atlaskit/avatar';
import PeopleGroupIcon from '@atlaskit/icon/core/migration/people-group';
import { Box, xcss } from '@atlaskit/primitives';
import LoadingSpinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import { borderRadius } from '@atlassian/jira-common-styles/src/main.tsx';
import { useOgTenantInfo } from '@atlassian/jira-eoc-tenant-info/src/controllers/index.tsx';
import { useFlagService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import type { OpsgenieTeam } from '@atlassian/jira-servicedesk-insight-object-types/src/common/types/object-details.tsx';
import useGetTeamById from '@atlassian/jira-servicedesk-services/src/services/get-platform-id/index.tsx';
import { Link } from '@atlassian/react-resource-router';
import { ReadViewContainer, PlaceholderSpacer } from '../../common/ui/styled.tsx';
import { messages } from './messages.tsx';

export const OpsgenieTeamReadView = ({ id, name, avatarUrl, url }: OpsgenieTeam) => {
	const { data, loading: teamLoading, error, fetchTeamInfo } = useGetTeamById();
	const { shouldUseJSM } = useOgTenantInfo();
	const { showFlag } = useFlagService();
	const platformTeamId = data?.platformTeamId;
	const teamUrl = shouldUseJSM ? `/jira/ops/teams/${platformTeamId}` : url;

	useEffect(() => {
		if (shouldUseJSM) {
			fetchTeamInfo(id);
		}
	}, [id, fetchTeamInfo, shouldUseJSM]);

	if (shouldUseJSM && teamLoading) {
		return (
			<Box xcss={loadingSpinnerStyle}>
				<LoadingSpinner />
			</Box>
		);
	}

	if (shouldUseJSM && error) {
		showFlag({
			type: 'error',
			title: messages.errorTitle,
			description: messages.errorMessage,
			messageId: 'servicedesk-insight-attribute-views.ui.opsgenie-team.show-flag.error',
			messageType: 'transactional',
		});
		return null;
	}

	if (shouldUseJSM && !teamLoading && !platformTeamId) {
		return null;
	}

	return (
		<OpsgenieTeamContainer>
			{avatarUrl && <Avatar appearance="square" size="xsmall" src={avatarUrl} />}
			{!avatarUrl && <PeopleGroupIcon LEGACY_size="small" label="" />}
			<NeutralLink href={teamUrl}>{name}</NeutralLink>
		</OpsgenieTeamContainer>
	);
};

export const OpsgenieTeamsReadView = ({
	opsgenieTeams,
	showPlaceholderIfEmpty = true,
}: {
	opsgenieTeams: OpsgenieTeam[];
	showPlaceholderIfEmpty?: boolean;
}) => {
	const { formatMessage } = useIntl();

	if (opsgenieTeams && opsgenieTeams.length === 0) {
		return (
			<ReadViewContainer isPlaceholder>
				<PlaceholderSpacer>
					{showPlaceholderIfEmpty ? formatMessage(messages.placeholder) : null}
				</PlaceholderSpacer>
			</ReadViewContainer>
		);
	}
	return (
		<ReadViewContainer isPlaceholder={false}>
			<OpsgenieTeamList>
				{opsgenieTeams.map(({ id, name, url, avatarUrl }) => (
					<OpsgenieTeamReadView key={id} id={id} name={name} url={url} avatarUrl={avatarUrl} />
				))}
			</OpsgenieTeamList>
		</ReadViewContainer>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const OpsgenieTeamList = styled.div({
	display: 'flex',
	flexWrap: 'wrap',
	gap: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const OpsgenieTeamContainer = styled.div({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.050'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.neutral'),
	paddingTop: token('space.025'),
	paddingRight: token('space.100'),
	paddingBottom: token('space.025'),
	paddingLeft: token('space.050'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `${borderRadius}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const NeutralLink = styled(Link)({
	color: token('color.text.subtle'),
	'&:hover': {
		color: token('color.text.subtle'),
	},
});

const loadingSpinnerStyle = xcss({
	display: 'flex',
	justifyContent: 'start',
});
